import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Settings, Play, Copy, CheckCircle, XCircle, Clock, ChevronDown, ChevronUp } from 'lucide-react';

interface TestCase {
  id: string;
  name: string;
  input: string;
  expectedOutput: string;
  signals: string[];
  keywords?: { required: string[]; denied: string[] };
  lastResult?: 'passed' | 'failed' | 'pending';
}

interface TestRun {
  id: string;
  commitSha: string;
  passed: boolean;
  passedTests: number;
  totalTests: number;
  createdAt: string;
  reportPreview?: string;
}

// Mock data
const mockTestCases: TestCase[] = [
  {
    id: 'test_1',
    name: 'Greeting response',
    input: 'Hello, I need help with billing',
    expectedOutput: "I'd be happy to help with your billing question.",
    signals: ['exact_match', 'length_ratio'],
    lastResult: 'passed',
  },
  {
    id: 'test_2',
    name: 'Escalation keywords',
    input: "I'm very unhappy and want to speak to a manager",
    expectedOutput: 'I understand your frustration. Let me connect you with a specialist.',
    signals: ['keyword_presence', 'length_ratio'],
    keywords: { required: ['understand', 'specialist'], denied: ['sorry'] },
    lastResult: 'passed',
  },
  {
    id: 'test_3',
    name: 'Refund request',
    input: 'I want a refund for my subscription',
    expectedOutput: 'I can help you with a refund. Let me process that for you.',
    signals: ['exact_match', 'keyword_presence'],
    lastResult: 'failed',
  },
  {
    id: 'test_4',
    name: 'Technical support',
    input: "The login page isn't loading",
    expectedOutput: "I'm sorry you're having trouble logging in. Let me help you troubleshoot.",
    signals: ['length_ratio', 'token_distribution'],
    lastResult: 'passed',
  },
];

const mockTestRuns: TestRun[] = [
  { id: 'run_1', commitSha: 'a1b2c3d', passed: true, passedTests: 4, totalTests: 4, createdAt: '2024-06-09T15:30:00Z' },
  { id: 'run_2', commitSha: 'e4f5g6h', passed: false, passedTests: 3, totalTests: 4, createdAt: '2024-06-09T12:15:00Z', reportPreview: 'Failed: Greeting response (length ratio: 1.45)' },
  { id: 'run_3', commitSha: 'i7j8k9l', passed: true, passedTests: 4, totalTests: 4, createdAt: '2024-06-08T18:45:00Z' },
  { id: 'run_4', commitSha: 'm0n1o2p', passed: true, passedTests: 4, totalTests: 4, createdAt: '2024-06-08T09:20:00Z' },
];

export default function PipelineDetail() {
  const { id } = useParams();
  const [testCases] = useState<TestCase[]>(mockTestCases);
  const [testRuns] = useState<TestRun[]>(mockTestRuns);
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const webhookUrl = `https://api.regressionguard.dev/webhook/${id}`;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const thresholds = {
    exact_match: { value: 0.8, adaptive: true },
    length_ratio: { value: 0.2, adaptive: false },
    keyword_presence: { value: 0, adaptive: true },
    token_distribution: { value: 2.0, adaptive: true },
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="flex items-center gap-2 text-text-secondary hover:text-text-primary transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Dashboard</span>
              </Link>
              <span className="text-text-secondary">/</span>
              <span className="text-text-primary font-medium text-sm">{id}</span>
            </div>

            <div className="flex items-center gap-3">
              <button className="inline-flex items-center gap-2 px-3 py-1.5 bg-pass/10 border border-pass/20 text-pass text-sm font-medium rounded-lg hover:bg-pass/20 transition-colors">
                <Play className="w-4 h-4" />
                Run Tests
              </button>
              <button className="p-2 text-text-secondary hover:text-text-primary transition-colors">
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pipeline header */}
        <div className="mb-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-mono font-bold text-text-primary mb-1">support-agent-v2</h1>
              <p className="text-text-secondary text-sm">Customer support agent production pipeline</p>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-accent/10 border border-accent/20 rounded text-accent text-xs font-medium">
                github_actions
              </span>
              <span className="px-2 py-1 bg-pass/10 border border-pass/20 rounded text-pass text-xs font-medium">
                Active
              </span>
            </div>
          </div>
        </div>

        {/* Integration settings */}
        <div className="bg-surface border border-border rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-text-primary mb-4">Integration Settings</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-text-secondary text-sm mb-2">Webhook URL</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={webhookUrl}
                  readOnly
                  className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-text-primary font-mono text-sm"
                />
                <button
                  onClick={() => copyToClipboard(webhookUrl)}
                  className="p-2 bg-background border border-border rounded-lg text-text-secondary hover:text-text-primary transition-colors"
                >
                  {copied ? <CheckCircle className="w-4 h-4 text-pass" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-text-secondary text-sm mb-2">API Key</label>
              <div className="flex items-center gap-2">
                <input
                  type="password"
                  value="rg_live_xxxxxxxxxxxxxxxxxxxx"
                  readOnly
                  className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-text-primary font-mono text-sm"
                />
                <button className="p-2 bg-background border border-border rounded-lg text-text-secondary hover:text-text-primary transition-colors">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Test Cases */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-text-primary">Test Cases</h2>
                <button className="text-sm text-accent hover:text-accent/80 transition-colors">+ Add Test Case</button>
              </div>
              <div className="bg-surface border border-border rounded-xl overflow-hidden">
                {testCases.map((tc, i) => (
                  <div key={tc.id} className={`px-4 py-4 ${i !== testCases.length - 1 ? 'border-b border-border' : ''}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-medium text-text-primary">{tc.name}</h3>
                          {tc.lastResult === 'passed' && <CheckCircle className="w-4 h-4 text-pass" />}
                          {tc.lastResult === 'failed' && <XCircle className="w-4 h-4 text-fail" />}
                          {tc.lastResult === 'pending' && <Clock className="w-4 h-4 text-warn" />}
                        </div>
                        <p className="text-text-secondary text-xs mb-2">Input: {tc.input.slice(0, 50)}...</p>
                        <div className="flex items-center gap-2">
                          {tc.signals.map(s => (
                            <span key={s} className="text-xs px-2 py-0.5 bg-background rounded text-text-secondary">
                              {s.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Test Run History */}
            <div>
              <h2 className="text-lg font-semibold text-text-primary mb-4">Test Run History</h2>
              <div className="bg-surface border border-border rounded-xl overflow-hidden">
                {testRuns.map((run, i) => (
                  <div key={run.id} className={`${i !== testRuns.length - 1 ? 'border-b border-border' : ''}`}>
                    <div
                      className="px-4 py-4 flex items-center justify-between cursor-pointer hover:bg-background/50 transition-colors"
                      onClick={() => setExpandedRun(expandedRun === run.id ? null : run.id)}
                    >
                      <div className="flex items-center gap-4">
                        {run.passed ? (
                          <CheckCircle className="w-5 h-5 text-pass" />
                        ) : (
                          <XCircle className="w-5 h-5 text-fail" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${run.passed ? 'text-pass' : 'text-fail'}`}>
                              {run.passed ? 'Passed' : 'Failed'}
                            </span>
                            <span className="text-text-secondary text-sm">
                              {run.passedTests}/{run.totalTests} tests
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-text-secondary text-xs mt-0.5">
                            <code className="bg-background px-1.5 py-0.5 rounded">{run.commitSha}</code>
                            <span>•</span>
                            <span>{new Date(run.createdAt).toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                      {expandedRun === run.id ? (
                        <ChevronUp className="w-4 h-4 text-text-secondary" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-text-secondary" />
                      )}
                    </div>

                    {expandedRun === run.id && (
                      <div className="px-4 pb-4">
                        <div className="bg-background rounded-lg p-4">
                          <h4 className="text-sm font-medium text-text-primary mb-2">Regression Report</h4>
                          <pre className="text-xs text-text-secondary font-mono overflow-x-auto">
{`## Regression Report ${run.passed ? '✅' : '❌'}

**Status:** ${run.passed ? 'PASSED' : 'FAILED'}
**Run ID:** ${run.id}
**Commit:** ${run.commitSha}
**Timestamp:** ${new Date(run.createdAt).toISOString()}

---

## Summary

- **Total Tests:** ${run.totalTests}
- **Passed:** ${run.passedTests}
- **Failed:** ${run.totalTests - run.passedTests}

---

## Test Results

${testCases.map(tc => {
  const passed = tc.lastResult === 'passed';
  return `${passed ? '✅' : '❌'} **${tc.name}**
  - exact_match: ${passed ? 'PASSED' : 'FAILED'}
  - length_ratio: PASSED
`;
}).join('\n')}`}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Threshold Configuration */}
            <div className="bg-surface border border-border rounded-xl p-5">
              <h3 className="font-semibold text-text-primary mb-4">Threshold Configuration</h3>
              <div className="space-y-4">
                {Object.entries(thresholds).map(([signal, config]) => (
                  <div key={signal}>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm text-text-secondary capitalize">
                        {signal.replace('_', ' ')}
                      </label>
                      {config.adaptive && (
                        <span className="text-xs px-1.5 py-0.5 bg-info/10 border border-info/20 rounded text-info">
                          adaptive
                        </span>
                      )}
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      defaultValue={config.value}
                      className="w-full h-1 bg-background rounded-lg appearance-none cursor-pointer accent-accent"
                    />
                    <div className="flex justify-between text-xs text-text-secondary mt-1">
                      <span>0</span>
                      <span className="text-text-primary font-mono">{config.value}</span>
                      <span>1</span>
                    </div>
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 px-3 py-2 border border-border hover:border-text-secondary text-text-secondary text-sm font-medium rounded-lg transition-colors">
                Reset to Defaults
              </button>
            </div>

            {/* Quick Stats */}
            <div className="bg-surface border border-border rounded-xl p-5">
              <h3 className="font-semibold text-text-primary mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary text-sm">Total Runs</span>
                  <span className="text-text-primary font-mono">47</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary text-sm">Pass Rate</span>
                  <span className="text-pass font-mono">89%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary text-sm">Last Run</span>
                  <span className="text-text-primary text-sm">2 hours ago</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-secondary text-sm">Avg Duration</span>
                  <span className="text-text-primary font-mono">1.2s</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}